import { Shape } from './Shape';
/**
 * Repeat `shape` on each point of `follow`
 *
 * @category Scene.Containers
 */
class ShapeFollow extends Shape {
    /**
     * Creates an instance of ShapeFollow.
     *
     * @param {IShapeFollowSettings} [settings={}]
     */
    constructor(settings) {
        settings.type = settings.type || 'ShapeFollow';
        super(settings);
        this.follow = settings.follow || settings.shape;
        this.bStatic = this.isStatic();
        this.bStaticIndexed = this.isStaticIndexed();
        this.shapeUseFollow = !!settings.shapeUseFollow;
        // this.currentGenerationRecursiveBounding = Bounding.empty()
    }
    /**
     *  Unset buffer
     *
     * @param {boolean} [bClearIndexed=false]
     * @param {boolean} [bPropagateToParents=false]
     * @param {boolean} [bPropagateToChildren=false]
     */
    clearBuffer(bClearIndexed = false, bPropagateToParents = true) {
        if (bClearIndexed) {
            this.shapeFollowBuffer = undefined;
        }
        super.clearBuffer(bClearIndexed, bPropagateToParents);
    }
    /**
     *
     * @returns {boolean}
     */
    isStatic() {
        return (this.follow ? this.follow.isStatic() : true) && super.isStatic();
    }
    /**
     *
     * @returns {boolean}
     */
    isStaticIndexed() {
        return (this.follow ? this.follow.isStaticIndexed() : true) && super.isStaticIndexed();
    }
    /**
     * Return a buffer of children shape with recursion
     *
     * @protected
     * @param {number} generateId
     * @param {PropArguments} propArguments
     * @returns {Float32Array}
     */
    generateBuffer(generateId, propArguments) {
        if (this.shape && this.follow) {
            if (typeof this.shapeFollowBuffer === 'undefined' ||
                this.shapeUseParent ||
                !this.shape.isGenerated(generateId) ||
                !this.follow.isGenerated(generateId)) {
                this.bindBuffer(generateId, propArguments);
            }
            return this.shapeFollowBuffer;
        }
        return Shape.EMPTY_BUFFER;
    }
    /**
     * Generate Recoursive shape buffer
     *
     * @protected
     * @param {number} generateId
     * @param {PropArguments} propArguments
     */
    bindBuffer(generateId, propArguments) {
        const followShape = this.follow;
        followShape.generate(generateId, false, propArguments);
        const followBuffer = followShape.getBuffer();
        // const followIndexed = followShape.getIndexedBuffer() as Array<IBufferIndex>
        const followPropArguments = {
            ...propArguments,
            follow: {
                index: 1,
                offset: 1,
                count: 1,
            },
            // follow_repetition: followIndexed[0].repetition,
        };
        const childShape = this.shape;
        childShape.generate(generateId, false, followPropArguments);
        let shapeBuffer = childShape.getBuffer();
        // const tmpBounding = [undefined, undefined, undefined, undefined]
        const singleShapeBufferLength = shapeBuffer.length;
        const followShapeBuffer = new Float32Array(singleShapeBufferLength * (followBuffer.length / 2));
        const totalFollowVertexCount = followBuffer.length / 2;
        for (let currentShapeFollowRepetition = 0; currentShapeFollowRepetition < totalFollowVertexCount; currentShapeFollowRepetition++) {
            const currentFollowRepetition = {
                index: currentShapeFollowRepetition + 1,
                offset: totalFollowVertexCount > 1 ? currentShapeFollowRepetition / (totalFollowVertexCount - 1) : 1,
                count: totalFollowVertexCount,
            };
            if (this.shapeUseFollow) {
                followPropArguments.follow = currentFollowRepetition;
                // followPropArguments.follow_repetition = followIndexed[currentShapeFollowRepetition].repetition
                childShape.generate(generateId, false, followPropArguments);
                shapeBuffer = childShape.getBuffer();
            }
            const shapeVertexBufferIndex = currentShapeFollowRepetition * singleShapeBufferLength;
            // const centerVertexIndex = parentRecursionBufferStartIndex + currentShapeRecursionRepetition * 2
            const centerVertexIndex = currentShapeFollowRepetition * 2;
            const centerX = followBuffer[centerVertexIndex];
            const centerY = followBuffer[centerVertexIndex + 1];
            for (let i = 0, len = shapeBuffer.length; i < len; i += 2) {
                followShapeBuffer[shapeVertexBufferIndex + i] = centerX + shapeBuffer[i];
                followShapeBuffer[shapeVertexBufferIndex + i + 1] = centerY + shapeBuffer[i + 1];
                // Bounding.add(
                // 	tmpBounding,
                // 	followShapeBuffer[shapeVertexBufferIndex + i],
                // 	followShapeBuffer[shapeVertexBufferIndex + i + 1]
                // )
            }
        }
        // Bounding.bind(this.currentGenerationRecursiveBounding, tmpBounding)
        this.shapeFollowBuffer = followShapeBuffer;
    }
    /**
     * Add this to indexedBuffer
     *
     * @protected
     * @param {number} frameLength
     * @param {IRepetition} repetition
     * @returns {number} nextIndex
     */
    addIndex(frameLength, repetition, singleRepetitionBounding) {
        const followBuffer = this.follow.getBuffer();
        if (this.shape && typeof followBuffer !== 'undefined') {
            // const propArguments = { repetition, shape: this } as IPropArguments as PropArguments
            const bufferIndex = {
                shape: this,
                frameLength: frameLength,
                singleRepetitionBounding,
                repetition: {
                    type: repetition.type,
                    angle: repetition.angle,
                    index: repetition.index,
                    count: repetition.count,
                    offset: repetition.offset,
                    row: {
                        index: repetition.row.index,
                        count: repetition.row.count,
                        offset: repetition.row.offset,
                    },
                    col: {
                        index: repetition.col.index,
                        count: repetition.col.count,
                        offset: repetition.col.offset,
                    },
                },
                follow: {
                    index: 1,
                    offset: 1,
                    count: 1,
                },
            };
            const childIndexedBuffer = this.shape.getIndexedBuffer() || [];
            for (let childIndexed = 0, childIndexedLen = childIndexedBuffer.length; childIndexed < childIndexedLen; childIndexed++) {
                const vertexCount = followBuffer.length / 2; // this.follow.getBufferLength({ ...propArguments, parent: { ...bufferIndex } }) / 2
                for (let j = 0, len = vertexCount; j < len; j++) {
                    const followOffset = len > 1 ? j / (len - 1) : 1;
                    for (let childIndexed = 0, childIndexedLen = childIndexedBuffer.length; childIndexed < childIndexedLen; childIndexed++) {
                        const currentIndexed = { ...childIndexedBuffer[childIndexed] };
                        const currentFollowRepetition = {
                            index: j + 1,
                            offset: followOffset,
                            count: len,
                        };
                        const followBufferIndex = { ...bufferIndex, follow: currentFollowRepetition };
                        const parent = currentIndexed.parent
                            ? Shape.setIndexedParent(currentIndexed.parent, followBufferIndex)
                            : followBufferIndex;
                        this.indexedBuffer.push({ ...currentIndexed, parent });
                    }
                }
            }
        }
    }
    /**
     * Retturn summation value
     *
     * @static
     * @param {number} recursion
     * @param {number} vertexCount
     * @returns {number}
     */
    static summmation(recursion, vertexCount) {
        if (recursion === 1)
            return 1;
        let result = 1;
        for (let i = 1; i < recursion; i++)
            result += vertexCount ** i;
        return result;
    }
    /**
     * Empty recursion repetition
     *
     * @static
     * @return {*}  {IRecursionRepetition}
     */
    static getEmptyRecursion() {
        return {
            index: 1,
            offset: 1,
            count: 1,
            level: { index: 1, offset: 1, count: 1 },
        };
    }
}
export { ShapeFollow };
//# sourceMappingURL=ShapeFollow.js.map